import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../context/ApiContext';
import { useNavigate } from 'react-router-dom';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import { Dropdown, DropdownItem } from '../components/ui/Dropdown';

export default function TenantsAdmin() {
  const { adminListTenants, adminSetTenantStatus, adminAddTenantDocument, adminRequestTenantPayment } = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pages, setPages] = useState([]); // items per page
  const [cursors, setCursors] = useState([null]); // cursor history
  const [currentPage, setCurrentPage] = useState(1); // 1-based index
  const [q, setQ] = useState('');
  const [property, setProperty] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);
  const [sendingPaymentId, setSendingPaymentId] = useState(null);

  useEffect(() => {
    resetAndFetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, property]);

  function resetAndFetchFirstPage(){
    setPages([]);
    setCursors([null]);
    setCurrentPage(1);
    fetchPageByIndex(1, { force: true });
  }

  async function fetchPageByIndex(pageIndex, { force = false } = {}) {
    try {
      setLoading(true);
      setError('');
      if (!force && pages[pageIndex - 1]) {
        setCurrentPage(pageIndex);
        return;
      }
      const cursor = cursors[pageIndex - 1] || null;
      const res = await adminListTenants({ status: 'active', limit: 25, cursor, q: q || undefined, property: property || undefined });
      const items = Array.isArray(res) ? res : (res?.items || []);
      const nextCursor = Array.isArray(res) ? null : (res?.nextCursor || null);
      setPages((prev) => {
        const copy = prev.slice();
        copy[pageIndex - 1] = items;
        return copy;
      });
      setCursors((prev) => {
        const copy = prev.slice();
        copy[pageIndex] = nextCursor;
        return copy;
      });
      setCurrentPage(pageIndex);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }

  async function markInactive(id){
    const reason = window.prompt('Reason (optional):', 'Vacated');
    await adminSetTenantStatus({ tenantId: id, status: 'Inactive', reason: reason || '' });
    resetAndFetchFirstPage();
  }

  async function addDocument(id){
    const name = window.prompt('Document name:', 'Other');
    if (!name) return;
    const fileName = window.prompt('File name (note: demo only, no upload):', 'document.pdf');
    await adminAddTenantDocument({ tenantId: id, document: { name, fileName } });
    resetAndFetchFirstPage();
  }

  async function promptPayment(t){
    try {
      setSendingPaymentId(t.id);
      const res = await adminRequestTenantPayment({ tenantId: t.id });
      alert(res?.message || 'Payment request email sent');
    } catch (e) {
      alert(e?.message || 'Failed to send payment request');
    } finally {
      setSendingPaymentId(null);
    }
  }

  const currentItems = pages[currentPage - 1] || [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Tenants</h1>
        <p className="text-gray-600">Active tenants with profiles and documents.</p>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <input className="border rounded px-3 py-2" placeholder="Search name/email/phone/ref" value={q} onChange={(e)=>setQ(e.target.value)} />
        <input className="border rounded px-3 py-2" placeholder="Filter by property slug" value={property} onChange={(e)=>setProperty(e.target.value)} />
        <Button onClick={() => resetAndFetchFirstPage()}>Refresh</Button>
        {loading && <span className="text-sm text-gray-500">Loading...</span>}
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {error}
          <div className="mt-2 flex gap-2">
            <button className="px-2 py-1 border rounded" onClick={() => resetAndFetchFirstPage()}>Retry</button>
            <button className="px-2 py-1 border rounded" onClick={async()=>{ try{ const h = await (await fetch('/api/health')).json(); setHealth(h);}catch(e){ setHealth({ error: e?.message || String(e) }); } }}>Check API Health</button>
          </div>
          {health && (<pre className="mt-2 text-xs overflow-auto max-h-64">{JSON.stringify(health,null,2)}</pre>)}
        </div>
      )}

      <div className="bg-white rounded-xl shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2">Tenant</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Property/Unit</th>
                <th className="text-left px-3 py-2">Lease</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && currentItems.length === 0 && (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`skel-${i}`} className="border-t">
                    <td className="px-3 py-3"><div className="h-4 w-40 bg-gray-200 rounded animate-pulse"/></td>
                    <td className="px-3 py-3"><div className="h-4 w-40 bg-gray-200 rounded animate-pulse"/></td>
                    <td className="px-3 py-3"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse"/></td>
                    <td className="px-3 py-3"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse"/></td>
                    <td className="px-3 py-3"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse"/></td>
                    <td className="px-3 py-3"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse"/></td>
                    <td className="px-3 py-3 text-right"><div className="h-8 w-24 bg-gray-200 rounded animate-pulse ml-auto"/></td>
                  </tr>
                ))
              )}
              {currentItems.map((t) => {
                const docs = t.documents || [];
                const avatar = t.profilePhoto || (docs.find(d => typeof d?.name==='string' && /profile/i.test(d.name) && d.url)?.url) || (docs.find(d => (d?.fileName||d?.url||'').toLowerCase().match(/\.(jpg|jpeg|png|webp)$/) && d.url)?.url);
                const statusVariant = t.status === 'Active' ? 'green' : 'gray';
                return (
                  <tr key={t.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        {avatar ? (<img src={avatar} alt={t.fullName || t.name} className="w-8 h-8 rounded-full object-cover" />) : (<div className="w-8 h-8 rounded-full bg-gray-200" />)}
                        <div>
                          <button className="font-medium hover:underline" onClick={() => navigate(`/admin/tenants/${encodeURIComponent(t.id)}`)}>{t.fullName || t.name}</button>
                          <div className="text-xs text-gray-500">ID: <button className="text-blue-600 hover:underline" onClick={() => navigate(`/admin/tenants/${encodeURIComponent(t.id)}`)}>{t.id}</button></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-gray-800">{t.email}</div>
                      <div className="text-xs text-gray-600">{t.phone}</div>
                    </td>
                    <td className="px-3 py-2"><Badge variant={statusVariant}>{t.status}</Badge></td>
                    <td className="px-3 py-2 text-xs text-gray-700">{t.propertySlug} • {t.unitId || '-'}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">{t.lease?.status || '-'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{t.updatedAt ? new Date(t.updatedAt).toLocaleString() : '-'}</td>
                    <td className="px-3 py-2 text-right">
                      <Dropdown>
                        <DropdownItem onClick={() => navigate(`/admin/tenants/${encodeURIComponent(t.id)}`)}>View Profile</DropdownItem>
                        <DropdownItem onClick={() => promptPayment(t)}>{sendingPaymentId === t.id ? 'Sending…' : 'Request Payment'}</DropdownItem>
                        <DropdownItem onClick={() => addDocument(t.id)}>Add Document</DropdownItem>
                        {user?.role === 'admin' && (
                          <DropdownItem danger onClick={() => markInactive(t.id)}>Move to Inactive</DropdownItem>
                        )}
                      </Dropdown>
                    </td>
                  </tr>
                );
              })}
              {!loading && currentItems.length === 0 && (
                <tr className="border-t"><td className="px-3 py-8 text-center text-gray-600" colSpan={7}>No tenants found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mt-3">
        <Button disabled={currentPage === 1 || loading} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
          Prev
        </Button>
        <span className="text-sm text-gray-700">Page {currentPage}</span>
        <Button
          disabled={loading || (!pages[currentPage] && !cursors[currentPage])}
          onClick={() => {
            const nextPage = currentPage + 1;
            if (pages[nextPage - 1]) {
              setCurrentPage(nextPage);
            } else if (cursors[currentPage]) {
              fetchPageByIndex(nextPage);
            }
          }}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
